import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { formatDateTime } from "../../lib/missions";
import { ENDED_STATUSES, REPORT_STATUS, nextStep, reportErrorMessage, waitingFor } from "../../lib/missionReports";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { FileText, RefreshCw } from "lucide-react";

// The mission page's « Compte rendu » card (Ordre 15): where the report stands, who must act, and the one next step.
export default function MissionReportCard({ mission }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const ended = ENDED_STATUSES.includes(mission.status);
  const load = useCallback(async () => {
    try {
      const { data: next } = await api.get(`/missions/${mission.id}/report`);
      setData(next);
      setError("");
    } catch (e) {
      setError(reportErrorMessage(e, "Impossible de charger le compte rendu."));
    }
  }, [mission.id]);
  useEffect(() => { if (ended) load(); }, [ended, load, mission.version]);

  if (!ended) return null;
  const status = data?.report ? REPORT_STATUS[data.report.status] : null;
  const step = nextStep(data);
  return (
    <Card className="p-5 space-y-3" data-testid="mission-report-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-slate-900 inline-flex items-center gap-2"><FileText size={16} className="text-slate-400" />Compte rendu</h3>
        {status && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.tone}`} data-testid="mission-report-status">{status.label}</span>}
      </div>
      {error ? (
        <div className="flex items-center justify-between gap-2 text-sm text-slate-600">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      ) : !data ? <Skeleton className="h-10 w-full" /> : (
        <>
          <p className="text-sm text-slate-600" data-testid="mission-report-waiting">
            {waitingFor(data) || (data.report?.review ? `${status?.short} par ${data.report.review.by_name} le ${formatDateTime(data.report.review.at, mission.timezone)}.`
              : data.report?.submitted_at ? `Publié par ${data.report.submitted_by_name} le ${formatDateTime(data.report.submitted_at, mission.timezone)}.` : "")}
          </p>
          {data.report?.review?.reason && data.report.status === "returned" && <p className="text-sm text-red-800">Motif : {data.report.review.reason}</p>}
          {step && (
            <Button asChild variant={step.key === "read" ? "outline" : "default"} data-testid="mission-report-open">
              <Link to={`/missions/${mission.id}/compte-rendu`}>{step.label}</Link>
            </Button>
          )}
        </>
      )}
    </Card>
  );
}
